import axios from "./_axios";
import { API } from "./apiEndPoints";

/** Map UI filter chips to API account status values. */
export function accountListStatusFilterToApi(status) {
  if (status === "pending activation") return "pending";
  return status;
}

function buildAccountListQueryParams(queryParams = {}) {
  const limit = queryParams.limit != null ? Number(queryParams.limit) : 10;
  const offset = queryParams.offset != null ? Number(queryParams.offset) : 0;
  const params = {
    limit,
    offset,
  };
  const search = queryParams.search;
  if (search != null && String(search).trim() !== "") {
    params.search = String(search).trim();
  }
  const rawStatus = queryParams.status;
  if (rawStatus != null) {
    const list = (Array.isArray(rawStatus) ? rawStatus : [rawStatus])
      .map(accountListStatusFilterToApi)
      .filter((s) => s != null && String(s).trim() !== "");
    if (list.length === 1) {
      params.status = list[0];
    } else if (list.length > 1) {
      params.status = list;
    }
  }
  return params;
}

/**
 * Serialize list query so repeated `status` keys work with typical Django-style APIs.
 * @param {Record<string, string|number|Array<string|number>>} params
 */
function serializeAccountListParams(params) {
  const parts = [];
  for (const [key, value] of Object.entries(params)) {
    if (value == null || value === "") continue;
    if (Array.isArray(value)) {
      for (const item of value) {
        if (item == null || item === "") continue;
        parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(String(item))}`);
      }
    } else {
      parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`);
    }
  }
  return parts.join("&");
}

/**
 * Admin account list (v2). Uses offset/limit pagination and optional `status` / `search`.
 *
 * @param {number} [legacyPage] Legacy: `getListOfAccounts(1, 500)` → limit/offset.
 * @param {number} [legacySize]
 * @param {{
 *   limit?: number,
 *   offset?: number,
 *   search?: string,
 *   status?: string|string[],
 * }} [query] Options object when first arg is not a number.
 */
export const getListOfAccounts = async (legacyPage, legacySize) => {
  let queryParams = {};
  if (typeof legacyPage === "number") {
    const page = legacyPage;
    const size = legacySize != null ? legacySize : 500;
    queryParams = {
      limit: size,
      offset: Math.max(0, (page - 1) * size),
    };
  } else if (legacyPage != null && typeof legacyPage === "object") {
    queryParams = legacyPage;
  }
  const params = buildAccountListQueryParams(queryParams);
  return axios()({
    method: "get",
    url: `${API.ACCOUNT_LIST_V2}`,
    params,
    paramsSerializer: serializeAccountListParams,
  });
};

export const saveAccount = async () => {
  try {
    let result = await axios()({
      method: "post",
      url: `${API.ACCOUNT_LIST_V2}`,
    });
    return result;
  } catch (error) {
    console.error("Error fetching account data:", error);
    throw error;
  }
};

export const createAccount = (data) => {
  let result = axios()({
    method: "post",
    url: `${API.ACCOUNT_LIST_V2}create/`,
    data: data,
  });
  return result;
};

export const renameAccount = (account_id, data) => {
  let result = axios()({
    method: "patch",
    url: `${API.ACCOUNT_LIST_V2}${account_id}/rename/`,
    data: data,
  });
  return result;
};

export const openAccount = (account_id, data) => {
  let result = axios()({
    method: "put",
    url: `${API.ACCOUNT_LIST_V2}${account_id}/open/`,
    data: data,
  });
  return result;
};

export const testEmail = (account_id, data) => {
  let result = axios()({
    method: "post",
    url: `${API.ACCOUNT_LIST_V2}${account_id}/test/email/`,
    data: data,
  });
  return result;
};

export const testEmailV1 = (account_id, data) => {
  let result = axios()({
    method: "post",
    url: `${API.ACCOUNTList}${account_id}/test/email/`,
    data: data,
  });
  return result;
};

export const changeOwner = (account_id, data) => {
  let result = axios()({
    method: "post",
    url: `${API.ACCOUNT_LIST_V2}${account_id}/change-owner/`,
    data: data,
  });
  return result;
};
export const reOpenAccount = (account_id) => {
  let result = axios()({
    method: "post",
    url: `${API.ACCOUNT_LIST_V2}${account_id}/re-open/`,
  });
  return result;
};

export const closeAccount = (account_id) => {
  let result = axios()({
    method: "post",
    url: `${API.ACCOUNT_LIST_V2}${account_id}/close/`,
  });
  return result;
};

export const suspendAccount = (account_id, data) => {
  let result = axios()({
    method: "post",
    url: `${API.ACCOUNT_LIST_V2}${account_id}/suspend/`,
    data: data,
  });
  return result;
};

export const deleteAccount = (account_id) => {
  let result = axios()({
    method: "delete",
    url: `${API.ACCOUNT_LIST_V2}${account_id}/delete/`,
  });
  return result;
};

export const getUserToken = (user_id) => {
  let result = axios()({
    method: "get",
    url: `https://api.dev.exiqtive.com/api/common/user/${user_id}/token/`,
  });
  return result;
};

export const recalculateReadiness = (account_id) => {
  return axios()({
    method: "post",
    url: `${API.ACCOUNT_LIST_V2}${account_id}/recalculate-readiness/`,
  });
};

/** GET admin portal account detail (includes product_* flags). */
export const getAccount = (account_id) => {
  return axios()({
    method: "get",
    url: `${API.ACCOUNT_LIST_V2}${account_id}/`,
  });
};

/**
 * Toggle a product module flag via Account.set_product_flag (ER-10465).
 * @param {string|number} account_id
 * @param {{ product: string, value: boolean }} data
 */
export const setProductFlag = (account_id, data) => {
  return axios()({
    method: "post",
    url: `${API.ACCOUNT_LIST_V2}${account_id}/set-product-flag/`,
    data,
  });
};
